import { marginTariff } from 'constants/tariffCodes';
import { productFamilyCodes } from 'types/productFamilyCodes';

const { MARGIN_TRAIN, MARGIN_TRAIN_ER } = marginTariff;

const trainMarginApplicableProductFamilyCodes = [productFamilyCodes.RIT];

/**
 * @typedef {Object} Extra
 * @property {boolean} cancelled
 * @property {string} code
 * @property {string} itemRef
 * @property {boolean} provisional
 */

/**
 * @typedef {Object} MarginExtra
 * @property {number} itemRefId
 * @property {number} price
 */

/**
 * @typedef {Object} ProductFamily
 * @property {string} code
 * @property {string} name
 */

/**
 * Are the product families in the legs' products applicable for train margins?
 *
 * @param {Leg[]} legs
 * @return {boolean}
 */
export const areLegProductsTrainMarginApplicable = (legs = []) => {
  const legProductCodesSet = new Set(
    legs.flatMap(({ products = [] }) => products).flatMap(({ productFamily: { familyGroup } }) => familyGroup)
  );
  const trainMarginApplicable = trainMarginApplicableProductFamilyCodes.some((code) => legProductCodesSet.has(code));
  return trainMarginApplicable;
};

/**
 * Are the product families in the booking applicable for train margins?
 *
 * @param {Object} booking
 * @param {ProductFamily[]} [booking.productFamilies=[]]
 * @return {boolean}
 */
export const areProductFamiliesTrainMarginApplicable = ({ productFamilies = [] }) => {
  const productFamilyCodes = new Set(productFamilies.map(({ familyGroup }) => familyGroup));
  const trainMarginApplicable = trainMarginApplicableProductFamilyCodes.some((code) => productFamilyCodes.has(code));
  return trainMarginApplicable;
};

/**
 * Return a MarginExtra with a priced calculation based on the supplied legs
 *
 * @param {Extra[]} [extras=[]] - the array of Extras to search for the pending MarginExtra
 * @param {Leg[]} [legs=[]] = [] - the child products will be searched to calculate the price of the MarginExtra
 * @param {number} [overridePrice] - if margins are found, this is the fixed price of the returned MarginExtra
 * @return {undefined|MarginExtra}
 */
export const calculateMargin = (extras = [], legs = [], overridePrice) => {
  const trainMarginExtra = getMarginExtraFromExtras(extras, false, true);

  if (!trainMarginExtra) {
    return;
  }

  const { itemRef } = trainMarginExtra;
  const itemRefId = parseInt(itemRef);

  if (overridePrice !== undefined) {
    return {
      itemRefId,
      price: overridePrice
    };
  }

  const currentPrice = legs
    .flatMap(({ products }) => products.map(({ cancelled, price, provisional }) => (provisional && !cancelled ? price : 0)))
    .reduce((total, item) => total + item, 0);
  const price = Math.ceil(currentPrice * 0.02);

  return { itemRefId, price };
};

/**
 * Return an array of margin tariff extras with a non-zero price, to be used for calls to override price
 *
 * @param {Object} params
 * @param {Extra[]} [params.outboundExtras=[]]
 * @param {Leg[]} [params.outbound=[]]
 * @param {Extra[]} [params.inboundExtras=[]]
 * @param {Leg[]} [params.inbound=[]] = []
 * @param {number} [params.outboundOverridePrice] - override price for the outbound MarginExtra
 * @param {number} [params.inboundOverridePrice] - override price for the inbound MarginExtra
 * @return {MarginExtra[]}
 */
export const calculateMargins = ({
  outboundExtras = [],
  outbound = [],
  inboundExtras = [],
  inbound = [],
  outboundOverridePrice,
  inboundOverridePrice
}) => {
  const outboundMargin = calculateMargin(outboundExtras, outbound, outboundOverridePrice);
  const inboundMargin = calculateMargin(inboundExtras, inbound, inboundOverridePrice);
  return [outboundMargin, inboundMargin].filter((margin) => margin !== undefined).filter(({ price }) => price !== 0);
};

/**
 * Return the first extra that has a train margin tariff code and matches the supplied conditions
 *
 * @param {Extra[]} [extras=[]]
 * @param {boolean} [cancelledCondition] - the extra should be cancelled, undefined will match either true or false
 * @param {boolean} [provisionalCondition] - the extra should be provisional, undefined will match either true or false
 * @return {undefined|Extra}
 */
export const getMarginExtraFromExtras = (extras = [], cancelledCondition, provisionalCondition) =>
  extras.find(({ cancelled, code, provisional }) => {
    const isCancelledOk = cancelledCondition === undefined ? true : cancelled === cancelledCondition;
    const isProvisionalOk = provisionalCondition === undefined ? true : provisional === provisionalCondition;
    const isCodeOk = code === MARGIN_TRAIN || code === MARGIN_TRAIN_ER;
    return isCancelledOk && isProvisionalOk && isCodeOk;
  });

/**
 * Return the applicable train margin tariff code
 *
 * @param {Object} params
 * @param {boolean} [params.isEROnly=false] - is the segment ER-only?
 * @return {MARGIN_TRAIN_ER|MARGIN_TRAIN}
 */
export const getMarginTariffCode = ({ isEROnly = false }) => (isEROnly ? MARGIN_TRAIN_ER : MARGIN_TRAIN);

/**
 * Are train margins applicable to this journey?
 *
 * @param {Object} params
 * @param {Journey} [params.outbound={}]
 * @param {Journey} [params.inbound={}]
 * @param {boolean} [params.isPackageBooking=false]
 * @return {boolean}
 */
export const isTrainMarginApplicable = ({ outbound = {}, inbound = {}, isPackageBooking = false }) => {
  const productFamilies = [outbound?.productFamily, inbound?.productFamily].filter((productFamily) => Boolean(productFamily));
  const trainMarginApplicable = areProductFamiliesTrainMarginApplicable({ productFamilies });
  return isPackageBooking && trainMarginApplicable;
};
